import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Copy, Mail, Search, ChevronRight, Filter, RotateCcw, List as ListIcon, UserPlus, Ghost, Users, Clock, ShieldCheck, Package, Hash, Link2, KeyRound, UserMinus, IdCard, Trophy } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import RolesPanel from "./RolesPanel";
import EquipmentLoansPanel from "./EquipmentLoansPanel";
import PlayHQIdsPanel from "./PlayHQIdsPanel";
import PlayHQLinksPanel from "./PlayHQLinksPanel";
import { CredentialsPanel } from "@/components/admin/CredentialsPanel";

const TYPE_OPTIONS = ["all", "Player", "Social", "Supporter", "Past Player", "Guest players"];
const GENDER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "male", label: "Men (incl. junior boys)" },
  { value: "female", label: "Women (incl. junior girls)" },
  { value: "junior", label: "Junior (under 18)" },
];
const CATEGORY_OPTIONS = [
  { value: "all", label: "All ages" },
  { value: "Junior", label: "Junior (< 18)" },
  { value: "Adult", label: "Adult (≥ 18)" },
];


function BFTeamTally({ team }) {
  const grade = team.name; // display name
  const teamId = team.team_id;
  const [tally, setTally] = useState(null);
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [gamesCount, setGamesCount] = useState(0);

  /* ── Admin edit state ── */
  const [editMatch, setEditMatch] = useState(null);       // which match card is expanded for admin editing
  const [matchVotes, setMatchVotes] = useState([]);        // all votes for the open match
  const [matchPlayers, setMatchPlayers] = useState([]);    // players for the open match
  const [loadingVotes, setLoadingVotes] = useState(false);
  const [voteDialog, setVoteDialog] = useState(null);      // {game_id, voter_user_id, voter_name, existing}
  const [voteSelections, setVoteSelections] = useState({}); // {player_uid: points}
  const [voteSaving, setVoteSaving] = useState(false);
  const [playerDialog, setPlayerDialog] = useState(null);   // game_id to add player to
  const [playerSearch, setPlayerSearch] = useState("");
  const [playerResults, setPlayerResults] = useState([]);
  const [playerSearching, setPlayerSearching] = useState(false);
  const [deleteVoteConfirm, setDeleteVoteConfirm] = useState(null); // {game_id, voter_user_id, voter_name}

  const loadTally = useCallback(() => {
    api.get(`/bf-votes/tally-by-team/${encodeURIComponent(teamId)}`).then(({ data }) => {
      setTally(data.tally || []);
      setGamesCount(data.games_count || 0);
      setMatches(data.matches || []);
    }).catch(() => { setTally([]); setMatches([]); });
  }, [teamId]);

  useEffect(() => {
    if (open && !tally) loadTally();
  }, [open, tally, teamId, loadTally]);

  /* ── Load votes & players for a specific match ── */
  const loadMatchAdmin = useCallback(async (gameId) => {
    setLoadingVotes(true);
    try {
      const [vRes, pRes] = await Promise.all([
        api.get(`/bf-votes/admin/${gameId}/all-votes`),
        api.get(`/bf-votes/admin/${gameId}/players`),
      ]);
      setMatchVotes(vRes.data.votes || []);
      setMatchPlayers(pRes.data.players || []);
    } catch { toast.error("Failed to load match details"); }
    setLoadingVotes(false);
  }, []);

  const toggleMatchEdit = (gameId) => {
    if (editMatch === gameId) { setEditMatch(null); return; }
    setEditMatch(gameId);
    loadMatchAdmin(gameId);
  };

  /* ── Vote editing ── */
  const openVoteDialog = (gameId, voterUserId, voterName, existingVotes) => {
    const initial = {};
    if (existingVotes) {
      existingVotes.forEach((v) => { initial[v.player_user_id] = v.points; });
    }
    setVoteSelections(initial);
    setVoteDialog({ game_id: gameId, voter_user_id: voterUserId, voter_name: voterName, existing: !!existingVotes });
  };

  const handleVoteSelect = (playerUid, points) => {
    setVoteSelections((prev) => {
      const next = { ...prev };
      // Remove this player if already assigned somewhere
      Object.keys(next).forEach((k) => { if (next[k] === points) delete next[k]; });
      // Remove this player's old assignment
      delete next[playerUid];
      // Assign
      next[playerUid] = points;
      return next;
    });
  };

  const clearVoteSelection = (playerUid) => {
    setVoteSelections((prev) => { const n = { ...prev }; delete n[playerUid]; return n; });
  };

  const saveVote = async () => {
    if (!voteDialog) return;
    const vals = Object.values(voteSelections).sort((a, b) => b - a);
    if (vals.length !== 3 || vals[0] !== 3 || vals[1] !== 2 || vals[2] !== 1) {
      toast.error("Assign exactly 3, 2, and 1 to three different players");
      return;
    }
    setVoteSaving(true);
    try {
      await api.put(`/bf-votes/admin/${voteDialog.game_id}/vote/${voteDialog.voter_user_id}`, { votes: voteSelections });
      toast.success(`Vote ${voteDialog.existing ? "updated" : "added"} for ${voteDialog.voter_name}`);
      setVoteDialog(null);
      loadMatchAdmin(voteDialog.game_id);
      loadTally();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save vote");
    }
    setVoteSaving(false);
  };

  const confirmDeleteVote = async () => {
    if (!deleteVoteConfirm) return;
    try {
      await api.delete(`/bf-votes/admin/${deleteVoteConfirm.game_id}/vote/${deleteVoteConfirm.voter_user_id}`);
      toast.success(`Vote deleted for ${deleteVoteConfirm.voter_name}`);
      setDeleteVoteConfirm(null);
      loadMatchAdmin(deleteVoteConfirm.game_id);
      loadTally();
    } catch { toast.error("Failed to delete vote"); }
  };

  /* ── Player management ── */
  const searchMembers = useCallback(async (q) => {
    if (q.length < 2) { setPlayerResults([]); return; }
    setPlayerSearching(true);
    try {
      const { data } = await api.get(`/bf-votes/admin/members-search?q=${encodeURIComponent(q)}`);
      setPlayerResults(data.members || []);
    } catch { setPlayerResults([]); }
    setPlayerSearching(false);
  }, []);

  useEffect(() => {
    if (!playerDialog) return;
    const t = setTimeout(() => searchMembers(playerSearch), 300);
    return () => clearTimeout(t);
  }, [playerSearch, playerDialog, searchMembers]);

  const addPlayer = async (member) => {
    try {
      await api.post(`/bf-votes/admin/${playerDialog}/player`, { user_id: member.user_id });
      toast.success(`${member.first_name} ${member.last_name} added`);
      loadMatchAdmin(playerDialog);
      loadTally();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add player");
    }
  };

  const removePlayer = async (gameId, userId, name, source) => {
    try {
      await api.delete(`/bf-votes/admin/${gameId}/player/${userId}?source=${source || "admin_added"}`);
      toast.success(`${name} removed`);
      loadMatchAdmin(gameId);
      loadTally();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove player");
    }
  };

  /* ── Points label helper ── */
  const pointsLabel = (pts) => pts === 3 ? "3 votes" : pts === 2 ? "2 votes" : "1 vote";

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Card
          className="px-3.5 py-3 rounded-xl border border-border hover:bg-muted/40 transition-colors cursor-pointer"
          data-testid={`bf-team-${grade.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <div className="text-sm font-medium truncate">{grade}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {tally ? `${tally.reduce((s, p) => s + p.total, 0)} votes across ${gamesCount} match${gamesCount === 1 ? "" : "es"}` : "Tap to view"}
          </div>
        </Card>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {open && (
          <div className="mt-1 mb-2 space-y-2">
            {/* Overall tally */}
            <div className="border border-border/60 rounded-lg bg-background overflow-hidden">
              <div className="px-3 py-2 border-b border-border/40 bg-muted/30">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Overall Leaderboard</div>
              </div>
              {!tally ? (
                <div className="text-xs text-muted-foreground text-center py-3">Loading…</div>
              ) : tally.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-3">No votes recorded yet</div>
              ) : (
                <div className="divide-y divide-border/40">
                  {tally.map((p, i) => (
                    <div key={p.user_id} className="flex items-center gap-2.5 px-3 py-2" data-testid={`bf-tally-row-${p.user_id}`}>
                      <span className="w-5 text-[11px] font-bold text-muted-foreground tabular-nums text-right shrink-0">{i + 1}.</span>
                      <div className="flex-1 min-w-0 text-sm font-medium truncate">{p.first_name} {p.last_name}</div>
                      <Badge variant="outline" className="text-xs font-bold tabular-nums bg-amber-500/10 text-amber-600 border-amber-500/30 shrink-0">
                        {p.total}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Per-match cards with admin editing */}
            {matches.map((m) => (
              <div key={m.game_id} className="border border-border/60 rounded-lg bg-background overflow-hidden" data-testid={`bf-match-${m.game_id}`}>
                <div
                  className="px-3 py-2 border-b border-border/40 bg-muted/30 flex items-center justify-between gap-2 cursor-pointer"
                  onClick={() => toggleMatchEdit(m.game_id)}
                >
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate">
                      {m.round ? `${m.round} vs` : 'vs'} {m.opponent}
                      {(m.status === "ABANDONED" || m.status === "FORFEIT") && (
                        <Badge variant="outline" className="ml-1.5 border-amber-500/40 text-amber-700 bg-amber-500/10 text-[9px] uppercase font-bold tracking-wider">{m.status === "ABANDONED" ? "Abandoned" : "Forfeit"}</Badge>
                      )}
                    </div>
                    {m.date && <div className="text-[10px] text-muted-foreground">{m.date}</div>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline" className="text-[10px]">{m.votes_count}/{m.total_selected || "?"} voted</Badge>
                    <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${editMatch === m.game_id ? "rotate-90" : ""}`} />
                  </div>
                </div>

                {/* Tally */}
                <div className="divide-y divide-border/40">
                  {m.tally.map((p, i) => (
                    <div key={p.user_id} className="flex items-center gap-2.5 px-3 py-1.5">
                      <span className="w-5 text-[10px] font-bold text-muted-foreground tabular-nums text-right shrink-0">{i + 1}.</span>
                      <div className="flex-1 min-w-0 text-xs font-medium truncate">{p.first_name} {p.last_name}</div>
                      <span className="text-xs font-bold tabular-nums text-amber-600 shrink-0">{p.total}</span>
                    </div>
                  ))}
                </div>

                {m.not_voted && m.not_voted.length > 0 && (
                  <div className="border-t border-border/40 bg-muted/20 px-3 py-2">
                    <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Haven't voted</div>
                    <div className="flex flex-wrap gap-1.5">
                      {m.not_voted.map((p) => (
                        <span key={p.user_id} className="text-[11px] text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5">{p.first_name} {p.last_name}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Admin: expanded edit panel ── */}
                {editMatch === m.game_id && (
                  <div className="border-t border-border/40 bg-muted/10 px-3 py-3 space-y-3" data-testid={`bf-admin-edit-${m.game_id}`}>
                    {loadingVotes ? (
                      <div className="text-xs text-muted-foreground text-center py-2">Loading…</div>
                    ) : (
                      <>
                        {/* All votes list */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">All Votes</div>
                            <Button
                              variant="outline" size="sm"
                              className="h-6 text-[10px] px-2 gap-1"
                              onClick={() => openVoteDialog(m.game_id, "", "", null)}
                              data-testid={`bf-admin-add-vote-${m.game_id}`}
                            >
                              <UserPlus className="h-3 w-3" /> Add Vote
                            </Button>
                          </div>
                          {matchVotes.length === 0 ? (
                            <div className="text-xs text-muted-foreground text-center py-2">No votes yet</div>
                          ) : (
                            <div className="space-y-1">
                              {matchVotes.map((v) => (
                                <div key={v.voter_user_id} className="flex items-center gap-2 bg-background rounded-lg border border-border/40 px-2.5 py-1.5" data-testid={`bf-admin-vote-${v.voter_user_id}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium truncate">
                                      {v.voter_first_name} {v.voter_last_name}
                                      {v.admin_edited && <span className="ml-1.5 text-[9px] text-muted-foreground font-normal italic">(admin)</span>}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground truncate">
                                      {v.votes.map((vt) => `${vt.first_name} ${vt.last_name} (${vt.points})`).join(" · ")}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-6 w-6 p-0 shrink-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                    onClick={() => setDeleteVoteConfirm({ game_id: m.game_id, voter_user_id: v.voter_user_id, voter_name: `${v.voter_first_name} ${v.voter_last_name}` })}
                                    data-testid={`bf-admin-delete-vote-${v.voter_user_id}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Players list */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Players</div>
                            <Button
                              variant="outline" size="sm"
                              className="h-6 text-[10px] px-2 gap-1"
                              onClick={() => { setPlayerDialog(m.game_id); setPlayerSearch(""); setPlayerResults([]); }}
                              data-testid={`bf-admin-add-player-${m.game_id}`}
                            >
                              <UserPlus className="h-3 w-3" /> Add Player
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {matchPlayers.map((p) => (
                              <span key={p.user_id} className={`inline-flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 ${p.source === "admin_added" ? "bg-blue-500/10 text-blue-600 border border-blue-500/30" : "bg-muted/60 text-muted-foreground"}`} data-testid={`bf-admin-player-${p.user_id}`}>
                                {p.first_name} {p.last_name}
                                <button
                                  className="hover:text-red-500 transition-colors ml-0.5"
                                  onClick={() => removePlayer(m.game_id, p.user_id, `${p.first_name} ${p.last_name}`, p.source)}
                                  data-testid={`bf-admin-remove-player-${p.user_id}`}
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleContent>

      {/* ── Vote add/edit dialog ── */}
      <Dialog open={!!voteDialog} onOpenChange={(o) => { if (!o) setVoteDialog(null); }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="bf-admin-vote-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{voteDialog?.existing ? "Edit Vote" : "Add Vote"}</DialogTitle>
          </DialogHeader>
          {voteDialog && (
            <div className="space-y-3">
              {/* Voter selector for new votes */}
              {!voteDialog.voter_user_id && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Voter (from haven't-voted list or search)</Label>
                  <Select onValueChange={(uid) => {
                    const match = matches.find((m) => m.game_id === voteDialog.game_id);
                    const notVoted = match?.not_voted || [];
                    const all = [...(matchPlayers || []), ...notVoted];
                    const voter = all.find((p) => p.user_id === uid);
                    setVoteDialog((prev) => ({
                      ...prev,
                      voter_user_id: uid,
                      voter_name: voter ? `${voter.first_name} ${voter.last_name}` : uid,
                    }));
                  }}>
                    <SelectTrigger className="h-9" data-testid="bf-admin-voter-select">
                      <SelectValue placeholder="Select voter…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const match = matches.find((m) => m.game_id === voteDialog.game_id);
                        const notVoted = match?.not_voted || [];
                        const allPlayers = matchPlayers || [];
                        const existingVoters = new Set(matchVotes.map((v) => v.voter_user_id));
                        // Show non-voted players first, then all players (deduplicated)
                        const seen = new Set();
                        const options = [];
                        notVoted.forEach((p) => {
                          if (!seen.has(p.user_id)) {
                            seen.add(p.user_id);
                            options.push(p);
                          }
                        });
                        allPlayers.forEach((p) => {
                          if (!seen.has(p.user_id) && !existingVoters.has(p.user_id)) {
                            seen.add(p.user_id);
                            options.push(p);
                          }
                        });
                        return options.map((p) => (
                          <SelectItem key={p.user_id} value={p.user_id}>{p.first_name} {p.last_name}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {voteDialog.voter_user_id && (
                <div className="text-sm font-medium">Voter: {voteDialog.voter_name}</div>
              )}

              {/* Player selection: 3-2-1 */}
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Assign 3, 2, 1 votes to players</Label>
                <div className="space-y-1 max-h-[40dvh] overflow-y-auto">
                  {matchPlayers.map((p) => {
                    const assigned = voteSelections[p.user_id];
                    return (
                      <div key={p.user_id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/30" data-testid={`bf-admin-vote-player-${p.user_id}`}>
                        <div className="flex-1 min-w-0 text-xs font-medium truncate">{p.first_name} {p.last_name}</div>
                        <div className="flex gap-1 shrink-0">
                          {[3, 2, 1].map((pts) => (
                            <button
                              key={pts}
                              className={`h-6 w-7 rounded text-[10px] font-bold transition-colors ${assigned === pts ? "bg-amber-500 text-white" : "bg-muted/60 text-muted-foreground hover:bg-muted"}`}
                              onClick={() => assigned === pts ? clearVoteSelection(p.user_id) : handleVoteSelect(p.user_id, pts)}
                              data-testid={`bf-admin-vote-${p.user_id}-${pts}`}
                            >
                              {pts}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Summary */}
              <div className="text-xs text-muted-foreground">
                Selected: {Object.entries(voteSelections).map(([uid, pts]) => {
                  const p = matchPlayers.find((pl) => pl.user_id === uid);
                  return p ? `${p.first_name} ${p.last_name} (${pts})` : `${uid} (${pts})`;
                }).join(", ") || "none"}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setVoteDialog(null)}>Cancel</Button>
            <Button size="sm" onClick={saveVote} disabled={voteSaving || !voteDialog?.voter_user_id} data-testid="bf-admin-vote-save">
              {voteSaving ? "Saving…" : "Save Vote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add player dialog ── */}
      <Dialog open={!!playerDialog} onOpenChange={(o) => { if (!o) setPlayerDialog(null); }}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="bf-admin-player-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Add Player</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search members…"
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              className="h-9"
              data-testid="bf-admin-player-search"
            />
            {playerSearching && <div className="text-xs text-muted-foreground text-center">Searching…</div>}
            <div className="space-y-1 max-h-[40dvh] overflow-y-auto">
              {playerResults.map((m) => {
                const alreadyIn = matchPlayers.some((p) => p.user_id === m.user_id);
                return (
                  <div key={m.user_id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/30" data-testid={`bf-admin-player-result-${m.user_id}`}>
                    <div className="flex-1 min-w-0 text-xs font-medium truncate">{m.first_name} {m.last_name}</div>
                    {alreadyIn ? (
                      <Badge variant="outline" className="text-[9px] shrink-0">Already in</Badge>
                    ) : (
                      <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => addPlayer(m)} data-testid={`bf-admin-add-player-btn-${m.user_id}`}>
                        Add
                      </Button>
                    )}
                  </div>
                );
              })}
              {playerSearch.length >= 2 && !playerSearching && playerResults.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-2">No members found</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete vote confirmation ── */}
      <AlertDialog open={!!deleteVoteConfirm} onOpenChange={(o) => { if (!o) setDeleteVoteConfirm(null); }}>
        <AlertDialogContent className="rounded-2xl" data-testid="bf-admin-delete-vote-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vote?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {deleteVoteConfirm?.voter_name}'s vote for this match.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteVote} className="bg-red-600 hover:bg-red-700" data-testid="bf-admin-delete-vote-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Collapsible>
  );
}


export default function AdminMembers() {
  const nav = useNavigate();
  const loc = useLocation();
  const [tab, setTab] = useState(null); // start with all cards closed
  // Deep-link support — /a/members?tab=playhq-links opens the suggestions tab
  // straight away (used by the "PlayHQ link suggestion" notification).
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const wanted = params.get("tab");
    if (wanted && ["approved","pending","rejected","roles","loans","playhq-ids","playhq-links","credentials","remove","change-id","club-awards"].includes(wanted)) {
      setTab(wanted);
    }
    // Intentionally only reruns on search string changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);
  const [items, setItems] = useState([]);
  // Section badge counts
  const [counts, setCounts] = useState(null);
  const refreshCounts = useCallback(() => {
    api.get("/members/section-counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, []);
  useEffect(() => { refreshCounts(); }, [refreshCounts]);
  // Approval flow — two-stage: (1) pick a PlayHQ candidate (or skip),
  // (2) confirm success with member id + email/phone chips.
  const [approveDialog, setApproveDialog] = useState(null); // post-approval success
  const [approvePicker, setApprovePicker] = useState(null); // pre-approval picker for a pending user
  const [approveBusy, setApproveBusy] = useState(false);
  const [pickerCandidates, setPickerCandidates] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerSelectedKey, setPickerSelectedKey] = useState(""); // "" means Skip
  const [priorRejections, setPriorRejections] = useState([]); // rejected rows matching the pending applicant
  const [rejecting, setRejecting] = useState(null);

  // Filter state (approved tab only)
  const [q, setQ] = useState("");
  const [mtype, setMtype] = useState("all");
  const [gender, setGender] = useState("all");
  const [category, setCategory] = useState("all");
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");

  // Guest → member promotion
  const [promoteTarget, setPromoteTarget] = useState(null); // guest user object
  const [promoteType, setPromoteType] = useState("Player");
  const [promoteSaving, setPromoteSaving] = useState(false);

  // Selection
  const [selected, setSelected] = useState(new Set());

  // List visibility — table is hidden by default on the Approved tab; admin opts in via the "List" button.
  const [listVisible, setListVisible] = useState(false);

  // Remove member state
  const [removeSearch, setRemoveSearch] = useState("");
  const [removeMembers, setRemoveMembers] = useState([]);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Club Awards — B&F teams (grouped by Derwent team, not grade)
  const [bfTeams, setBfTeams] = useState(null);
  useEffect(() => {
    if (tab === "club-awards" && !bfTeams) {
      api.get("/bf-votes/teams").then(({ data }) => {
        setBfTeams(data?.teams || []);
      }).catch(() => setBfTeams([]));
    }
  }, [tab, bfTeams]);

  const loadRemoveMembers = useCallback(async () => {
    setRemoveLoading(true);
    try {
      const { data } = await api.get("/members", { params: { q: removeSearch || undefined } });
      setRemoveMembers(Array.isArray(data) ? data : []);
    } catch { setRemoveMembers([]); }
    setRemoveLoading(false);
  }, [removeSearch]);

  useEffect(() => {
    if (tab !== "remove") return;
    const t = setTimeout(loadRemoveMembers, 300);
    return () => clearTimeout(t);
  }, [tab, removeSearch, loadRemoveMembers]);

  const handleRemoveMember = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      const { data } = await api.delete(`/admin/members/${confirmRemove.id}`);
      toast.success(data.message || "Member removed");
      setConfirmRemove(null);
      loadRemoveMembers();
      refreshCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove member");
    }
    setRemoving(false);
  };

  // Change Member ID state
  const [midSearch, setMidSearch] = useState("");
  const [midMembers, setMidMembers] = useState([]);
  const [midLoading, setMidLoading] = useState(false);
  const [midEditing, setMidEditing] = useState(null);
  const [midNewValue, setMidNewValue] = useState("");
  const [midSaving, setMidSaving] = useState(false);

  const loadMidMembers = useCallback(async () => {
    setMidLoading(true);
    try {
      const { data } = await api.get("/members", { params: { q: midSearch || undefined } });
      setMidMembers(Array.isArray(data) ? data : []);
    } catch { setMidMembers([]); }
    setMidLoading(false);
  }, [midSearch]);

  useEffect(() => {
    if (tab !== "change-id") return;
    const t = setTimeout(loadMidMembers, 300);
    return () => clearTimeout(t);
  }, [tab, midSearch, loadMidMembers]);

  const handleChangeMemberId = async () => {
    if (!midEditing || !midNewValue.trim()) return;
    setMidSaving(true);
    try {
      const { data } = await api.post(`/admin/members/${midEditing.id}/reset-member-id`, { new_member_id: midNewValue.trim() });
      toast.success(`Member ID changed: ${data.old_member_id} → ${data.new_member_id}`);
      setMidEditing(null);
      setMidNewValue("");
      loadMidMembers();
      refreshCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change Member ID");
    }
    setMidSaving(false);
  };

  const buildParams = () => {
    if (tab !== "approved") return {};
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (mtype && mtype !== "all") params.membership_type = mtype;
    if (gender && gender !== "all") params.gender = gender;
    if (category && category !== "all") params.category = category;
    if (minAge !== "" && !Number.isNaN(parseInt(minAge))) params.min_age = parseInt(minAge);
    if (maxAge !== "" && !Number.isNaN(parseInt(maxAge))) params.max_age = parseInt(maxAge);
    return params;
  };

  const load = async () => {
    // These tabs manage their own data — nothing to fetch here.
    if (!tab || tab === "roles" || tab === "loans" || tab === "playhq-ids" || tab === "playhq-links" || tab === "credentials" || tab === "remove" || tab === "change-id") return;
    try {
      if (tab === "approved") {
        const { data } = await api.get("/members", { params: buildParams() });
        setItems(data || []);
      } else {
        const { data } = await api.get("/applications/member", { params: { status: tab } });
        setItems(data || []);
      }
      setSelected(new Set());
    } catch (e) {
      toast.error("Could not load members");
    }
  };

  // Reload whenever tab or filters change (debounced on q)
  useEffect(() => {
    if (!tab) return;
    if (tab !== "approved") { load(); return; }
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [tab, q, mtype, gender, category, minAge, maxAge]);

  const openApproveDialog = async (u) => {
    // Open the picker first so admin can attach a PlayHQ id (or skip).
    setApprovePicker(u);
    setPickerSelectedKey("");
    setPickerSearch("");
    setPriorRejections([]);
    setPickerLoading(true);
    try {
      const [candRes, priorRes] = await Promise.all([
        api.get("/playhq/admin/players/picker"),
        api.get(`/applications/member/${u.id}/prior-rejections`).catch(() => ({ data: [] })),
      ]);
      setPickerCandidates(candRes.data || []);
      setPriorRejections(priorRes.data || []);
      // Best-guess pre-selection: exact case-insensitive name match.
      const target = `${u.first_name || ""} ${u.last_name || ""}`.trim().toLowerCase();
      const hit = (candRes.data || []).find((r) => r.name_key === target);
      if (hit) setPickerSelectedKey(hit.name_key);
    } catch (e) {
      toast.error("Could not load PlayHQ candidates");
      setPickerCandidates([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const confirmApprove = async () => {
    if (!approvePicker) return;
    setApproveBusy(true);
    try {
      const chosen = pickerCandidates.find((r) => r.name_key === pickerSelectedKey);
      const body = {
        ...(chosen && chosen.playhq_id ? { playhq_id: chosen.playhq_id, playhq_name_key: chosen.name_key } : {}),
        // Include any prior rejection ids so the backend hard-deletes
        // them after the successful approval. Empty list is a no-op.
        remove_prior_rejection_ids: priorRejections.map((r) => r.id),
      };
      const { data } = await api.post(`/applications/member/${approvePicker.id}/approve`, body);
      // If we linked a PlayHQ id, check whether a guest player already
      // owns that id (or shares the same name). If so surface a merge
      // prompt in the success dialog.
      let guestCandidate = null;
      try {
        // The approval endpoint returned a member_id, and the new user's
        // id equals approvePicker.id. Use that to look up guest overlap.
        const gRes = await api.get("/playhq/admin/guest-merge-candidate", {
          params: { member_user_id: approvePicker.id, playhq_id: chosen?.playhq_id || "" },
        });
        guestCandidate = gRes.data?.guest || null;
      } catch { /* silent — merge is optional */ }
      setApproveDialog({
        user: approvePicker,
        member_id: data.member_id,
        otp: data.otp,
        member_user_id: approvePicker.id,
        email: data.email,
        phone: data.phone,
        playhq_id: data.playhq_id || "",
        playhq_name: chosen?.name || "",
        guest_candidate: guestCandidate,
      });
      setApprovePicker(null);
      load();
      refreshCounts();
    } catch (e) { toast.error("Approve failed"); }
    finally { setApproveBusy(false); }
  };

  const mergeGuestFromDialog = async () => {
    if (!approveDialog?.guest_candidate || !approveDialog.member_user_id) return;
    setApproveBusy(true);
    try {
      await api.post("/playhq/admin/guest-merge", {
        member_user_id: approveDialog.member_user_id,
        guest_user_id: approveDialog.guest_candidate.id,
      });
      toast.success("Stats history merged into new member");
      setApproveDialog((s) => s ? { ...s, guest_candidate: null } : s);
      load();
      refreshCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setApproveBusy(false); }
  };

  const rejectAgainFromPicker = async () => {
    // Called when the admin has been warned about a prior rejection
    // and decides to reject the current pending applicant too. Closes
    // the picker cleanly and reuses the existing reject endpoint.
    if (!approvePicker) return;
    setApproveBusy(true);
    try {
      await api.post(`/applications/member/${approvePicker.id}/reject`);
      toast.success("Application rejected");
      setApprovePicker(null);
      setPriorRejections([]);
      load();
      refreshCounts();
    } catch (e) {
      toast.error("Reject failed");
    } finally { setApproveBusy(false); }
  };
  const performReject = async () => {
    if (!rejecting) return;
    try { await api.post(`/applications/member/${rejecting.id}/reject`); toast.success("Application rejected"); setRejecting(null); load(); refreshCounts(); }
    catch { toast.error("Reject failed"); }
  };

  // Selection helpers
  const allVisibleSelected = items.length > 0 && items.every((u) => selected.has(u.id));
  const someVisibleSelected = !allVisibleSelected && items.some((u) => selected.has(u.id));
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelected((prev) => { const s = new Set(prev); items.forEach((u) => s.delete(u.id)); return s; });
    } else {
      setSelected((prev) => { const s = new Set(prev); items.forEach((u) => s.add(u.id)); return s; });
    }
  };
  const toggleOne = (id) => setSelected((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const clearSelection = () => setSelected(new Set());

  const selectedUsers = useMemo(() => items.filter((u) => selected.has(u.id)), [items, selected]);
  const selectedWithEmail = useMemo(() => selectedUsers.filter((u) => (u.email || "").trim()), [selectedUsers]);

  const openMailClient = () => {
    if (!selectedUsers.length) { toast.error("Select at least one member"); return; }
    if (!selectedWithEmail.length) { toast.error("Selected members have no email on file"); return; }
    const bcc = selectedWithEmail.map((u) => u.email).join(",");
    // mailto: with empty `to:` and the list in BCC for privacy. Subject pre-filled, body left blank.
    const subject = `Derwent Cricket Club — ${selectedWithEmail.length} member${selectedWithEmail.length === 1 ? "" : "s"}`;
    window.location.href = `mailto:?bcc=${encodeURIComponent(bcc)}&subject=${encodeURIComponent(subject)}`;
    toast.success(`Opening mail client with ${selectedWithEmail.length} BCC recipient${selectedWithEmail.length === 1 ? "" : "s"}`);
  };

  const resetFilters = () => {
    setQ(""); setMtype("all"); setGender("all"); setCategory("all"); setMinAge(""); setMaxAge("");
  };

  const filtersActive = q || mtype !== "all" || gender !== "all" || category !== "all" || minAge !== "" || maxAge !== "";

  const openPromote = (u) => {
    setPromoteTarget(u);
    setPromoteType("Player");
  };
  const doPromote = async () => {
    if (!promoteTarget) return;
    setPromoteSaving(true);
    try {
      const { data } = await api.post(`/members/${promoteTarget.id}/promote`, {
        membership_type: promoteType,
      });
      toast.success(
        `${data.first_name} ${data.last_name} promoted to ${data.membership_type} · ${data.member_id}`
      );
      setPromoteTarget(null);
      load();
      refreshCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    } finally {
      setPromoteSaving(false);
    }
  };

  // Inline change of an existing member's membership_type (Player → Past Player, etc).
  // Uses optimistic UI: flip the row immediately, revert on error.
  const [changingTypeId, setChangingTypeId] = useState(null);
  const changeMembershipType = async (user, nextType) => {
    if (!user || !nextType || user.membership_type === nextType) return;
    // Optimistic update
    setItems((prev) => prev.map((u) => (u.id === user.id ? { ...u, membership_type: nextType } : u)));
    setChangingTypeId(user.id);
    try {
      await api.patch(`/members/${user.id}/membership-type`, { membership_type: nextType });
      toast.success(`${user.first_name} ${user.last_name} → ${nextType}`);
      refreshCounts();
    } catch (e) {
      // Revert
      setItems((prev) => prev.map((u) => (u.id === user.id ? { ...u, membership_type: user.membership_type } : u)));
      toast.error(e?.response?.data?.detail || "Couldn't change membership type");
    } finally {
      setChangingTypeId(null);
    }
  };

  // ---- Shared members table used by approved / pending / rejected sections ----
  const renderMembersTable = () => (
    <Card className="mt-3 overflow-x-auto rounded-2xl" data-testid="members-table-card">
      <Table>
        <TableHeader>
          <TableRow>
            {tab === "approved" && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleAllVisible}
                  aria-label="Select all"
                  data-testid="members-select-all"
                />
              </TableHead>
            )}
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Type</TableHead>
            {tab === "approved" && <TableHead>Age</TableHead>}
            {tab === "approved" && <TableHead>Gender</TableHead>}
            <TableHead>Member ID</TableHead>
            {tab !== "approved" && <TableHead>Status</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={tab === "approved" ? 9 : 7} className="text-center text-muted-foreground py-8">
                {tab === "approved" && filtersActive ? "No members match the current filters." : "No records."}
              </TableCell>
            </TableRow>
          )}
          {items.map((u) => {
            const isSel = selected.has(u.id);
            return (
              <TableRow key={u.id} className={isSel ? "bg-[hsl(var(--accent))]/5" : ""} data-testid={`members-row-${u.id}`}>
                {tab === "approved" && (
                  <TableCell>
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={() => toggleOne(u.id)}
                      aria-label={`Select ${u.first_name} ${u.last_name}`}
                      data-testid={`members-row-check-${u.id}`}
                    />
                  </TableCell>
                )}
                <TableCell>
                  {tab === "approved" ? (
                    u.is_guest_player ? (
                      <div>
                        <span className="inline-flex items-center gap-1.5 font-medium" data-testid={`members-row-name-${u.id}`}>
                          {u.first_name} {u.last_name}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <button
                          type="button"
                          onClick={() => nav(`/a/members/${u.id}`)}
                          className="text-left font-medium hover:text-[hsl(var(--accent))] inline-flex items-center gap-1.5 transition-colors"
                          data-testid={`members-row-name-${u.id}`}
                        >
                          {u.first_name} {u.last_name}
                          <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100" />
                        </button>
                      </div>
                    )
                  ) : (
                    <div>
                      <span>{u.first_name} {u.last_name}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{u.email || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{u.phone || "—"}</TableCell>
                <TableCell>
                  {u.is_guest_player ? (
                    <button
                      type="button"
                      onClick={() => openPromote(u)}
                      className="inline-flex items-center gap-1 rounded-md border border-amber-400 bg-amber-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-800 font-semibold hover:bg-amber-100 hover:border-amber-500 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300"
                      data-testid={`members-row-guest-badge-${u.id}`}
                      title="Guest player — click to promote to full member"
                    >
                      <Ghost className="h-3 w-3" /> Guest
                    </button>
                  ) : tab === "approved" && u.member_id ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-left rounded-md px-2 py-0.5 -mx-2 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/40 transition-colors disabled:opacity-60"
                          disabled={changingTypeId === u.id}
                          data-testid={`members-row-type-${u.id}`}
                          title="Click to change membership type"
                        >
                          {u.membership_type || "—"}
                          {changingTypeId === u.id && <span className="ml-1 text-xs text-muted-foreground">…</span>}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-56 p-1.5" data-testid={`members-row-type-popover-${u.id}`}>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground px-2 pt-1 pb-1.5">
                          Change membership type
                        </div>
                        {["Player", "Social", "Supporter", "Past Player"].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={(e) => {
                              changeMembershipType(u, t);
                              e.currentTarget.closest("[data-state='open']")?.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
                            }}
                            disabled={u.membership_type === t}
                            className="w-full text-left text-sm px-2 py-1.5 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-default flex items-center justify-between transition-colors"
                            data-testid={`members-row-type-option-${u.id}-${t.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            <span>{t}</span>
                            {u.membership_type === t && <Check className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />}
                          </button>
                        ))}
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <span>{u.membership_type || "—"}</span>
                  )}
                </TableCell>
                {tab === "approved" && <TableCell>{u.age ?? "—"}</TableCell>}
                {tab === "approved" && (
                  <TableCell className="capitalize text-muted-foreground">{u.gender || "—"}</TableCell>
                )}
                <TableCell className="font-mono-sm">{u.member_id || (u.is_guest_player ? <span className="text-amber-800 italic text-xs">unassigned</span> : "—")}</TableCell>
                {tab !== "approved" && (
                  <TableCell><Badge variant={u.status === "approved" ? "default" : "outline"}>{u.status}</Badge></TableCell>
                )}
                <TableCell className="text-right space-x-2">
                  {tab === "pending" && (
                    <>
                      <Button size="sm" onClick={() => openApproveDialog(u)} data-testid={`approve-${u.id}`} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]">
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setRejecting(u)} data-testid={`reject-${u.id}`}>
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {tab === "approved" && (
                    u.is_guest_player ? (
                      <Button
                        size="sm"
                        className="rounded-full bg-amber-600 text-white hover:bg-amber-700"
                        onClick={() => openPromote(u)}
                        data-testid={`members-row-promote-${u.id}`}
                        title="Convert this guest player into a full member"
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Promote
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => nav(`/a/members/${u.id}`)} data-testid={`members-row-open-${u.id}`}>
                        Open profile
                      </Button>
                    )
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );

  return (
    <AppShell variant="admin" title="Members">
      <Accordion
        type="single"
        collapsible
        value={tab}
        onValueChange={(v) => setTab(v || "")}
        className="space-y-2"
      >
        {/* ---- Approved ---- */}
        <AccordionItem value="approved" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-approved">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-approved">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <Users className="h-4 w-4 text-[hsl(var(--accent))]" /> Approved Members
              {counts?.approved != null && <Badge variant="secondary" className="ml-1 rounded-full px-2 py-0 text-[11px] font-medium">{counts.approved}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            {/* Filters & bulk actions */}
            <Card className="p-4 rounded-2xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  <Filter className="h-3.5 w-3.5" /> Search & filter
                </div>
                {filtersActive && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={resetFilters} data-testid="members-reset-filters">
                    <RotateCcw className="h-3 w-3 mr-1" /> Reset
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                <div className="col-span-2 lg:col-span-2">
                  <Label className="text-xs">Name / email</Label>
                  <div className="relative mt-1">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search first, last or email…"
                      className="h-10 pl-9 rounded-lg"
                      data-testid="members-search-q"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={mtype} onValueChange={setMtype}>
                    <SelectTrigger className="h-10 mt-1 rounded-lg" data-testid="members-filter-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All types" : t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Gender</Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger className="h-10 mt-1 rounded-lg" data-testid="members-filter-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Age category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="h-10 mt-1 rounded-lg" data-testid="members-filter-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Age range</Label>
                  <div className="flex items-center gap-1.5 mt-1">
                    <Input
                      type="number" min="0" max="120"
                      value={minAge}
                      onChange={(e) => setMinAge(e.target.value)}
                      placeholder="Min"
                      className="h-10 rounded-lg"
                      data-testid="members-filter-min-age"
                    />
                    <span className="text-muted-foreground">–</span>
                    <Input
                      type="number" min="0" max="120"
                      value={maxAge}
                      onChange={(e) => setMaxAge(e.target.value)}
                      placeholder="Max"
                      className="h-10 rounded-lg"
                      data-testid="members-filter-max-age"
                    />
                  </div>
                </div>
              </div>

              {/* Bulk-action bar */}
              <div className="mt-4 flex items-center justify-between flex-wrap gap-3 border-t border-border pt-3">
                <div className="text-xs text-muted-foreground" data-testid="members-selection-summary">
                  {selected.size === 0
                    ? `${items.length} member${items.length === 1 ? "" : "s"} found`
                    : <>
                        <span className="font-semibold text-foreground">{selected.size}</span> selected
                        <button type="button" onClick={clearSelection} className="ml-2 text-[hsl(var(--accent))] hover:underline" data-testid="members-clear-selection">Clear</button>
                      </>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant={listVisible ? "default" : "outline"}
                    className={`rounded-full ${listVisible ? "bg-[hsl(var(--fg))] text-[hsl(var(--bg))] hover:bg-[hsl(var(--fg))]/90" : ""}`}
                    onClick={() => setListVisible((v) => !v)}
                    data-testid="members-toggle-list"
                    title={listVisible ? "Hide the members list" : "Show the members list"}
                  >
                    <ListIcon className="h-3.5 w-3.5 mr-1.5" /> {listVisible ? "Hide list" : "List"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={openMailClient}
                    disabled={selected.size === 0}
                    data-testid="members-email-selected"
                    title={selected.size === 0 ? "Select members first" : "Open default mail app with BCC list"}
                  >
                    <Mail className="h-3.5 w-3.5 mr-1.5" /> Email selected
                  </Button>
                </div>
              </div>
            </Card>
            {listVisible && renderMembersTable()}
          </AccordionContent>
        </AccordionItem>

        {/* ---- Remove Members ---- */}
        <AccordionItem value="remove" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-remove">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-remove">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <UserMinus className="h-4 w-4 text-red-400" /> Remove Members
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <Card className="p-4 rounded-2xl">
              <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                <Search className="h-3.5 w-3.5" /> Find member to remove
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={removeSearch}
                  onChange={(e) => setRemoveSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="h-10 pl-9 rounded-lg"
                  data-testid="remove-member-search"
                />
              </div>
            </Card>

            <Card className="mt-3 overflow-x-auto rounded-2xl" data-testid="remove-members-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Member ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {removeLoading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">Loading…</TableCell>
                    </TableRow>
                  )}
                  {!removeLoading && removeMembers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        {removeSearch ? "No members match your search." : "No members found."}
                      </TableCell>
                    </TableRow>
                  )}
                  {!removeLoading && removeMembers.map((u) => (
                    <TableRow key={u.id} data-testid={`remove-row-${u.id}`}>
                      <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email || "—"}</TableCell>
                      <TableCell className="font-mono text-sm">{u.member_id || "—"}</TableCell>
                      <TableCell>{u.membership_type || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                          onClick={() => setConfirmRemove(u)}
                          data-testid={`remove-btn-${u.id}`}
                        >
                          <UserMinus className="h-3.5 w-3.5 mr-1.5" /> Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </AccordionContent>
        </AccordionItem>

        {/* ---- Pending ---- */}
        <AccordionItem value="pending" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-pending">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-pending">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <Clock className="h-4 w-4 text-amber-500" /> Pending Members
              {counts?.pending != null && counts.pending > 0 && <Badge className="ml-1 rounded-full px-2 py-0 text-[11px] font-medium bg-amber-500 text-white hover:bg-amber-500">{counts.pending}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            {renderMembersTable()}
          </AccordionContent>
        </AccordionItem>

        {/* ---- Rejected ---- */}
        <AccordionItem value="rejected" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-rejected">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-rejected">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <X className="h-4 w-4 text-red-500" /> Rejected Members
              {counts?.rejected != null && counts.rejected > 0 && <Badge variant="secondary" className="ml-1 rounded-full px-2 py-0 text-[11px] font-medium">{counts.rejected}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            {renderMembersTable()}
          </AccordionContent>
        </AccordionItem>

        {/* ---- Roles & Committee ---- */}
        <AccordionItem value="roles" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-roles">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-roles">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <ShieldCheck className="h-4 w-4 text-indigo-500" /> Roles & Committee
              {counts?.roles != null && <Badge variant="secondary" className="ml-1 rounded-full px-2 py-0 text-[11px] font-medium">{counts.roles}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <RolesPanel onDataChange={refreshCounts} />
          </AccordionContent>
        </AccordionItem>

        {/* ---- PlayHQ IDs ---- */}
        <AccordionItem value="playhq-ids" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-playhq-ids">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-playhq-ids">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <Hash className="h-4 w-4 text-sky-500" /> PlayHQ IDs/Edit Member Stats
              {counts?.playhq_ids != null && <Badge variant="secondary" className="ml-1 rounded-full px-2 py-0 text-[11px] font-medium">{counts.playhq_ids}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <PlayHQIdsPanel onDataChange={refreshCounts} />
          </AccordionContent>
        </AccordionItem>

        {/* ---- PlayHQ Links ---- */}
        <AccordionItem value="playhq-links" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-playhq-links">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-playhq-links">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <Link2 className="h-4 w-4 text-violet-500" /> New PlayHQ Links
              {counts?.playhq_links != null && counts.playhq_links > 0 && <Badge className="ml-1 rounded-full px-2 py-0 text-[11px] font-medium bg-violet-500 text-white hover:bg-violet-500">{counts.playhq_links}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <PlayHQLinksPanel onDataChange={refreshCounts} />
          </AccordionContent>
        </AccordionItem>

        {/* ---- Credentials ---- */}
        <AccordionItem value="credentials" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-credentials">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-credentials">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <KeyRound className="h-4 w-4 text-orange-500" /> Change/Reset Password
              {counts?.credentials_total != null && <Badge variant="secondary" className="ml-1 rounded-full px-2 py-0 text-[11px] font-medium">{counts.credentials_with_password}/{counts.credentials_total}</Badge>}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <CredentialsPanel onDataChange={refreshCounts} />
          </AccordionContent>
        </AccordionItem>

        {/* ---- Change Member ID ---- */}
        <AccordionItem value="change-id" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-change-id">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-change-id">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <IdCard className="h-4 w-4 text-cyan-500" /> Change Member ID
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <Card className="p-4 rounded-2xl">
              <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                <Search className="h-3.5 w-3.5" /> Find member
              </div>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={midSearch}
                  onChange={(e) => setMidSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="h-10 pl-9 rounded-lg"
                  data-testid="change-id-search"
                />
              </div>
            </Card>

            <Card className="mt-3 overflow-x-auto rounded-2xl" data-testid="change-id-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Current Member ID</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {midLoading && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-8">Loading…</TableCell>
                    </TableRow>
                  )}
                  {!midLoading && midMembers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                        {midSearch ? "No members match your search." : "No members found."}
                      </TableCell>
                    </TableRow>
                  )}
                  {!midLoading && midMembers.map((u) => (
                    <TableRow key={u.id} data-testid={`change-id-row-${u.id}`}>
                      <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                      <TableCell className="font-mono text-sm">{u.member_id || "—"}</TableCell>
                      <TableCell className="text-right">
                        {midEditing?.id === u.id ? (
                          <div className="flex items-center gap-2 justify-end">
                            <Input
                              value={midNewValue}
                              onChange={(e) => setMidNewValue(e.target.value)}
                              placeholder="e.g. 0099"
                              className="h-8 w-36 text-sm rounded-lg"
                              data-testid={`change-id-input-${u.id}`}
                            />
                            <Button
                              size="sm"
                              className="rounded-full h-8"
                              onClick={handleChangeMemberId}
                              disabled={midSaving || !midNewValue.trim()}
                              data-testid={`change-id-save-${u.id}`}
                            >
                              {midSaving ? "…" : "Save"}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="rounded-full h-8"
                              onClick={() => { setMidEditing(null); setMidNewValue(""); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            onClick={() => { setMidEditing(u); setMidNewValue(u.member_id || ""); }}
                            data-testid={`change-id-btn-${u.id}`}
                          >
                            <IdCard className="h-3.5 w-3.5 mr-1.5" /> Change ID
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </AccordionContent>
        </AccordionItem>

        {/* ---- Club Awards ---- */}
        <AccordionItem value="club-awards" className="border rounded-xl px-4 data-[state=open]:bg-muted/20" data-testid="members-tab-club-awards">
          <AccordionTrigger className="hover:no-underline py-3" data-testid="members-trigger-club-awards">
            <span className="flex items-center gap-2.5 text-sm font-semibold">
              <Trophy className="h-4 w-4 text-amber-500" /> Club Awards
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <Card className="p-4 rounded-2xl" data-testid="club-awards-bf-votes">
              <div className="flex items-center gap-2.5 mb-3">
                <Trophy className="h-4 w-4 text-amber-500" />
                <div className="text-sm font-semibold">B&F Votes</div>
              </div>
              {!bfTeams ? (
                <div className="text-xs text-muted-foreground text-center py-4">Loading teams…</div>
              ) : bfTeams.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-4">No teams with B&F-eligible games found.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {bfTeams.map((team) => (
                    <BFTeamTally key={team.team_id} team={team} />
                  ))}
                </div>
              )}
            </Card>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* ============ Approve — PlayHQ picker (pre-approval) ============ */}
      <Dialog open={!!approvePicker} onOpenChange={(o) => !o && !approveBusy && (setApprovePicker(null), setPriorRejections([]))}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="approve-picker-dialog">
          <DialogHeader>
            <DialogTitle>Approve member</DialogTitle>
          </DialogHeader>
          {approvePicker && (
            <div className="space-y-4">
              {priorRejections.length > 0 && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-3 text-sm" data-testid="approve-prior-rejections">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-red-500 flex items-center gap-1.5">
                    <span aria-hidden>⚠</span> Previously rejected
                  </div>
                  <div className="mt-1">
                    This applicant matches {priorRejections.length} past rejected application{priorRejections.length === 1 ? "" : "s"}.
                    Approving will override the earlier decision and remove the old record{priorRejections.length === 1 ? "" : "s"} from the rejected list.
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {priorRejections.map((r) => (
                      <div key={r.id} className="rounded-lg bg-background/60 border border-red-500/20 p-2 text-xs flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {r.first_name} {r.last_name}
                            {r.date_of_birth && <span className="text-muted-foreground font-normal"> · {r.date_of_birth}</span>}
                          </div>
                          <div className="text-muted-foreground truncate">
                            {r.email || "no email"}{r.phone ? ` · ${r.phone}` : ""}
                          </div>
                          {r.reject_reason && (
                            <div className="text-muted-foreground italic mt-0.5 truncate">Reason: {r.reject_reason}</div>
                          )}
                        </div>
                        {(r.rejected_at || r.created_at) && (
                          <div className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                            {new Date(r.rejected_at || r.created_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={rejectAgainFromPicker}
                      disabled={approveBusy}
                      className="rounded-full border-red-500/40 text-red-600 hover:bg-red-500/10 hover:text-red-700"
                      data-testid="approve-picker-reject-again"
                    >
                      Reject again
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-sm text-muted-foreground">
                Approving{" "}
                <span className="text-foreground font-medium">
                  {approvePicker.first_name} {approvePicker.last_name}
                </span>. Optionally link them to their PlayHQ profile so
                their scorecard appearances start counting immediately.
              </div>

              <div className="rounded-xl border border-border">
                <div className="p-3 border-b border-border">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      placeholder="Search PlayHQ players…"
                      className="pl-9 h-10 rounded-lg"
                      data-testid="approve-picker-search"
                    />
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto p-2">
                  {pickerLoading && (
                    <div className="text-sm text-muted-foreground p-4 text-center">Loading candidates…</div>
                  )}
                  {!pickerLoading && (() => {
                    const q = pickerSearch.trim().toLowerCase();
                    const filtered = q
                      ? pickerCandidates.filter((r) => (r.name || "").toLowerCase().includes(q))
                      : pickerCandidates.slice(0, 60);
                    if (!filtered.length) {
                      return <div className="text-sm text-muted-foreground p-4 text-center">No matches. Use "Skip — not in PlayHQ yet" below.</div>;
                    }
                    return filtered.map((r) => (
                      <button
                        type="button"
                        key={r.name_key}
                        onClick={() => setPickerSelectedKey(r.name_key)}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between gap-3 transition-colors ${
                          pickerSelectedKey === r.name_key
                            ? "bg-[hsl(var(--accent))]/15 border border-[hsl(var(--accent))]/40"
                            : "hover:bg-[hsl(var(--secondary))]/60 border border-transparent"
                        } ${r.already_linked ? "opacity-60" : ""}`}
                        data-testid={`approve-picker-row-${r.name_key}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-2">
                            {r.name}
                            {r.is_current_season && (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]">This season</span>
                            )}
                            {r.already_linked && (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Already linked</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {r.appearance_count} appearance{r.appearance_count === 1 ? "" : "s"}{r.latest_season ? ` · latest ${r.latest_season}` : ""}
                          </div>
                        </div>
                        {pickerSelectedKey === r.name_key && <Check className="h-4 w-4 text-[hsl(var(--accent))] shrink-0" />}
                      </button>
                    ));
                  })()}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setPickerSelectedKey("")}
                className={`w-full text-left rounded-xl border p-3 transition-colors ${
                  pickerSelectedKey === ""
                    ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5"
                    : "border-dashed border-border hover:bg-[hsl(var(--secondary))]/40"
                }`}
                data-testid="approve-picker-skip"
              >
                <div className="text-sm font-medium">Skip — not in PlayHQ yet</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Approve without a PlayHQ id. We'll notify the member managers as soon as their name shows up in a finalised match so you can link them then.
                </div>
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setApprovePicker(null); setPriorRejections([]); }} disabled={approveBusy} data-testid="approve-picker-cancel">Cancel</Button>
            <Button onClick={confirmApprove} disabled={approveBusy} data-testid="approve-picker-confirm" className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]">
              {approveBusy
                ? "Approving…"
                : priorRejections.length > 0
                  ? (pickerSelectedKey ? "Approve anyway & link" : "Approve anyway")
                  : pickerSelectedKey
                    ? "Approve & link"
                    : "Approve without PlayHQ id"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Approval dialog (existing) ============ */}
      <Dialog open={!!approveDialog} onOpenChange={(o) => !o && setApproveDialog(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Member approved</DialogTitle></DialogHeader>
          {approveDialog && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">{approveDialog.user.first_name} {approveDialog.user.last_name}</span> is now an approved member. Share the Member ID below so they can sign in to the Members Portal.
              </div>
              <div className="rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Member ID</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="font-mono-sm text-2xl">{approveDialog.member_id}</div>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => { navigator.clipboard.writeText(approveDialog.member_id); toast.success("Copied"); }}><Copy className="h-4 w-4 mr-1" /> Copy</Button>
                </div>
              </div>
              {/* PlayHQ link status chip so admin sees at a glance whether
                  the join happened during approval. Falls back to a nudge
                  when no id was picked. */}
              <div className="rounded-xl border border-border p-3 text-sm flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">PlayHQ link</div>
                  <div className="mt-1 truncate">
                    {approveDialog.playhq_id
                      ? <>Linked to <span className="font-medium">{approveDialog.playhq_name || approveDialog.playhq_id}</span></>
                      : <>Not linked yet — we'll notify member managers when their name appears in a finalised match.</>}
                  </div>
                </div>
                {approveDialog.playhq_id ? (
                  <Badge variant="outline" className="rounded-full border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))]">Linked</Badge>
                ) : (
                  <Badge variant="secondary" className="rounded-full">Unlinked</Badge>
                )}
              </div>

              {/* Guest-merge prompt — a guest player record shadows this
                  member. Offer to fold the guest into the member so their
                  historical stats join the new profile cleanly. */}
              {approveDialog.guest_candidate && (() => {
                const gs = approveDialog.guest_candidate.stats || {};
                const hasStats = (gs.matches || 0) > 0;
                return (
                  <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-sm" data-testid="approve-guest-merge">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-500">Existing guest player</div>
                        <div className="mt-1">
                          A guest record for{" "}
                          <span className="font-medium">
                            {approveDialog.guest_candidate.first_name} {approveDialog.guest_candidate.last_name}
                          </span>{" "}
                          already exists.
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={mergeGuestFromDialog}
                        disabled={approveBusy}
                        className="rounded-full h-8 px-3 shrink-0 bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="approve-guest-merge-btn"
                      >
                        Merge history
                      </Button>
                    </div>
                    {hasStats ? (
                      <div className="mt-3 grid grid-cols-4 gap-2" data-testid="approve-guest-merge-stats">
                        {[
                          {label: "Matches", value: gs.matches},
                          {label: "Runs", value: gs.runs},
                          {label: "Wickets", value: gs.wickets},
                          {label: "Catches", value: gs.catches},
                        ].map((s) => (
                          <div key={s.label} className="rounded-lg bg-background/60 border border-amber-500/20 p-2 text-center">
                            <div className="text-xs text-muted-foreground">{s.label}</div>
                            <div className="text-lg font-semibold tabular-nums">{s.value ?? 0}</div>
                          </div>
                        ))}
                        {(gs.seasons || 0) > 0 && (
                          <div className="col-span-4 text-[11px] text-muted-foreground text-center pt-1">
                            {gs.seasons} season{gs.seasons === 1 ? "" : "s"}
                            {gs.latest_season ? ` · latest ${gs.latest_season}` : ""}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-muted-foreground">No historical stats recorded against this guest yet.</div>
                    )}
                  </div>
                );
              })()}
              <div className="rounded-xl border border-border p-4 space-y-3 text-sm">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Share with the new member</div>

                {/* OTP display */}
                {approveDialog.otp && (
                  <div className="rounded-lg bg-[hsl(var(--secondary))]/60 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">One-time password</div>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="font-mono text-lg tracking-wider">{approveDialog.otp}</div>
                      <Button variant="outline" size="sm" className="rounded-full h-7 text-xs" onClick={() => { navigator.clipboard.writeText(approveDialog.otp); toast.success("OTP copied"); }} data-testid="approve-copy-otp"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">They'll use this to log in for the first time, then set their own password.</p>
                  </div>
                )}

                {/* Send welcome email button */}
                {approveDialog.email && (
                  <a
                    href={`mailto:${approveDialog.email}?subject=${encodeURIComponent("Welcome to Derwent Cricket Club!")}&body=${encodeURIComponent(
`Hi ${approveDialog.user.first_name},

Great news — your membership has been approved! Welcome to Derwent Cricket Club.

Here are your login details:

Member ID: ${approveDialog.member_id}
One-time Password: ${approveDialog.otp || "(see admin)"}

Log in here: ${window.location.origin}/member-login

When you first log in, you'll be asked to set your own password.

See you at the club!
Derwent Cricket Club`)}`}
                    className="block"
                    data-testid="approve-send-email"
                  >
                    <Button type="button" className="w-full rounded-full h-10 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white gap-2" data-testid="approve-send-email-btn">
                      <Mail className="h-4 w-4" /> Send welcome email to {approveDialog.email}
                    </Button>
                  </a>
                )}

                {/* Fallback contact info */}
                <div className="flex items-center justify-between gap-3">
                  {approveDialog.email && (
                    <Button variant="ghost" size="sm" className="rounded-full h-7 text-xs gap-1.5 text-muted-foreground" onClick={() => { navigator.clipboard.writeText(approveDialog.email); toast.success("Email copied"); }}><Copy className="h-3 w-3" /> {approveDialog.email}</Button>
                  )}
                  {approveDialog.phone && (
                    <Button variant="ghost" size="sm" className="rounded-full h-7 text-xs gap-1.5 text-muted-foreground" onClick={() => { navigator.clipboard.writeText(approveDialog.phone); toast.success("Phone copied"); }}><Copy className="h-3 w-3" /> {approveDialog.phone}</Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ Reject confirmation ============ */}
      <AlertDialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this application?</AlertDialogTitle>
            <AlertDialogDescription>
              {rejecting ? <>&ldquo;<span className="font-medium">{rejecting.first_name} {rejecting.last_name}</span>&rdquo; will be moved to the Rejected list and they won&rsquo;t be issued a Member ID.</> : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={performReject} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Reject</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============ Promote guest → full member ============ */}
      <Dialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="members-promote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-amber-600" /> Promote guest player
            </DialogTitle>
          </DialogHeader>
          {promoteTarget && (
            <div className="space-y-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-semibold">Currently a guest player</div>
                <div className="mt-1 font-medium">{promoteTarget.first_name} {promoteTarget.last_name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Their stats history will be preserved — the same record just gets a Member ID and a membership type.
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Membership type</Label>
                <Select value={promoteType} onValueChange={setPromoteType}>
                  <SelectTrigger className="mt-1.5 h-10 rounded-lg" data-testid="members-promote-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Player">Player — active player this season</SelectItem>
                    <SelectItem value="Past Player">Past Player — historical, appears in stats only</SelectItem>
                    <SelectItem value="Social">Social</SelectItem>
                    <SelectItem value="Supporter">Supporter</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  A fresh <span className="font-mono">0001</span>-style Member ID will be assigned automatically.
                  Email, phone and DOB can be filled in later from the member&rsquo;s profile.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPromoteTarget(null)} className="rounded-full" disabled={promoteSaving}>Cancel</Button>
            <Button
              onClick={doPromote}
              disabled={promoteSaving}
              className="rounded-full bg-amber-600 text-white hover:bg-amber-700"
              data-testid="members-promote-confirm"
            >
              {promoteSaving ? "Promoting…" : "Promote to member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Remove member confirmation ============ */}
      <AlertDialog open={!!confirmRemove} onOpenChange={(v) => !v && setConfirmRemove(null)}>
        <AlertDialogContent data-testid="remove-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold text-foreground">{confirmRemove?.first_name} {confirmRemove?.last_name}</span> ({confirmRemove?.member_id || "no ID"}) and all their related data including equipment loans.
              <br /><br />This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="remove-member-confirm"
            >
              {removing ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
